import { useCallback, useRef, useState } from 'react';

export interface SwipeGesture {
  direction: 'N' | 'NE' | 'E' | 'SE' | 'S' | 'SW' | 'W' | 'NW';
  distance: number;
  velocity: number;
  startPosition: { x: number; y: number };
  endPosition: { x: number; y: number };
}

export interface TouchGestureOptions {
  minSwipeDistance?: number;
  minSwipeVelocity?: number;
  maxSwipeTime?: number;
  onSwipe?: (gesture: SwipeGesture) => void;
  onTap?: (position: { x: number; y: number }) => void;
  onLongPress?: (position: { x: number; y: number }) => void;
  longPressDelay?: number;
}

export function useTouchGestures(options: TouchGestureOptions = {}) {
  const {
    minSwipeDistance = 25,
    minSwipeVelocity = 0.2,
    maxSwipeTime = 1000,
    onSwipe,
    onTap,
    onLongPress,
    longPressDelay = 350
  } = options;

  const touchStartRef = useRef<{ x: number; y: number; time: number } | null>(null);
  const longPressTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const getDirection = useCallback((dx: number, dy: number): SwipeGesture['direction'] => {
    const angle = Math.atan2(dy, dx) * 180 / Math.PI;
    const normalizedAngle = (angle + 360) % 360;
    
    if (normalizedAngle >= 337.5 || normalizedAngle < 22.5) return 'E';
    if (normalizedAngle >= 22.5 && normalizedAngle < 67.5) return 'SE';
    if (normalizedAngle >= 67.5 && normalizedAngle < 112.5) return 'S';
    if (normalizedAngle >= 112.5 && normalizedAngle < 157.5) return 'SW';
    if (normalizedAngle >= 157.5 && normalizedAngle < 202.5) return 'W';
    if (normalizedAngle >= 202.5 && normalizedAngle < 247.5) return 'NW';
    if (normalizedAngle >= 247.5 && normalizedAngle < 292.5) return 'N';
    return 'NE';
  }, []);

  const clearLongPressTimer = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  const handleTouchStart = useCallback((e: TouchEvent | React.TouchEvent) => {
    if (e.touches.length !== 1) return;
    
    const touch = e.touches[0];
    touchStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now()
    };
    
    setIsDragging(false);
    clearLongPressTimer();

    // Start long press timer
    if (onLongPress) {
      longPressTimerRef.current = setTimeout(() => {
        if (touchStartRef.current && !isDragging) {
          onLongPress({ x: touchStartRef.current.x, y: touchStartRef.current.y });
          clearLongPressTimer();
        }
      }, longPressDelay);
    }

    // Prevent default to avoid scrolling
    e.preventDefault();
  }, [onLongPress, longPressDelay, isDragging, clearLongPressTimer]);

  const handleTouchMove = useCallback((e: TouchEvent | React.TouchEvent) => {
    if (!touchStartRef.current || e.touches.length !== 1) return;

    const touch = e.touches[0];
    const dx = touch.clientX - touchStartRef.current.x;
    const dy = touch.clientY - touchStartRef.current.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    // If we've moved enough, consider it a drag
    if (distance > 10) {
      setIsDragging(true);
      clearLongPressTimer();
    }

    e.preventDefault();
  }, [clearLongPressTimer]);

  const handleTouchEnd = useCallback((e: TouchEvent | React.TouchEvent) => {
    clearLongPressTimer();

    if (!touchStartRef.current) return;

    const touch = e.changedTouches[0];
    const dx = touch.clientX - touchStartRef.current.x;
    const dy = touch.clientY - touchStartRef.current.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    const time = Date.now() - touchStartRef.current.time;
    const velocity = distance / time;

    if (distance >= minSwipeDistance && velocity >= minSwipeVelocity && time <= maxSwipeTime) {
      // It's a swipe
      if (onSwipe) {
        const gesture: SwipeGesture = {
          direction: getDirection(dx, dy),
          distance,
          velocity,
          startPosition: { x: touchStartRef.current.x, y: touchStartRef.current.y },
          endPosition: { x: touch.clientX, y: touch.clientY }
        };
        onSwipe(gesture);
      }
    } else if (!isDragging && onTap) {
      // It's a tap
      onTap({ x: touchStartRef.current.x, y: touchStartRef.current.y });
    }

    touchStartRef.current = null;
    setIsDragging(false);
  }, [minSwipeDistance, minSwipeVelocity, maxSwipeTime, onSwipe, onTap, isDragging, getDirection, clearLongPressTimer]);

  const gestureHandlers = {
    onTouchStart: handleTouchStart,
    onTouchMove: handleTouchMove,
    onTouchEnd: handleTouchEnd
  };

  return {
    gestureHandlers,
    isDragging
  };
}